import { assert, runInDebug } from '@ember/debug';

/**
 * Records which capabilities are present in a composed class. Statics are inherited through
 * `extends`, so reading this off a class surfaces everything applied below it in the chain rather
 * than only its own step.
 */
const APPLIED = Symbol.for('spds:applied-capabilities');

const appliedTo = Class => Class?.[APPLIED] ?? new Set();

/**
 * Builds a class from a base plus the core capabilities it opts into, applied left to right:
 *
 * ```js
 * export default class SpdsPanelCollapsible extends compose(Super, Animated, Dismissible) {}
 * ```
 *
 * A capability is applied at most once per chain. That matters because capabilities compose with
 * one another - `Dismissible` applies `Animated` for itself - so a component naming both would
 * otherwise carry `Animated` twice, duplicating its `classesToMap` entry and its metadata.
 *
 * @param {Function} Base - the class the component already extends
 * @param {...Function} capabilities - capability factories from `spds/core/capability`
 * @returns {Function} a subclass of `Base` carrying each capability
 */
export const compose = (Base, ...capabilities) =>
  capabilities.reduce((Class, capability) => {
    runInDebug(() => {
      assert(
        `spds/capabilities: compose expects capability factories, received ${typeof capability}`,
        typeof capability === 'function'
      );
    });

    if (appliedTo(Class).has(capability)) return Class;

    const Composed = capability(Class);

    // Read before assigning: the lookup walks the prototype chain, so this picks up whatever the
    // capability applied for itself before recording the capability.
    Composed[APPLIED] = new Set([...appliedTo(Composed), capability]);

    return Composed;
  }, Base);
